import { createLogic } from 'redux-logic';
import { order as orderApi } from '@squareup/sq-addons-web-sdk';

import {
  ADD_COMP_DISCOUNT,
  CLEAR_COMP_DISCOUNTS,
  FETCH_COMP_DISCOUNTS,
  REMOVE_COMP_DISCOUNT,
  REMOVE_COMP_DISCOUNTS,
  SUBMIT_COMP_DISCOUNTS,
} from './types';
import {
  addCompDiscountFulfilled,
  addCompDiscountRejected,
  clearCompDiscountsFulfilled,
  clearCompDiscountsRejected,
  fetchCompDiscountsFulfilled,
  fetchCompDiscountsRejected,
  removeCompDiscountFulfilled,
  removeCompDiscountRejected,
  removeCompDiscountsFulfilled,
  removeCompDiscountsRejected,
  submitCompDiscountsRejected,
} from './actions';
import { setPointBalance } from 'store/customer';
import { syncAppliedCompCards } from 'store/searchCompCards';
import { simulateAccrualAndRedemption } from 'store/api/discounts/simulateAccrualAndRedemption';
import { redeemOnly } from 'store/api/discounts/redeemOnly';
import { getCompCardsFromOrder } from 'store/api/discounts/utils/discountUtil';

export const addCompDiscountLogic = createLogic({
  type: ADD_COMP_DISCOUNT,
  cancelType: CLEAR_COMP_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;
    let discountId = '';

    cancelled$.subscribe(async () => {
      cancelled = true;
    });

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;

    const { cardId, discount } = action.payload;
    discount.metadata.pxCompCardId = cardId?.referenceId;

    try {
      // Add the discount to the order.
      discountId = await orderApi.addDiscount(discount);
      const updatedOrder = await orderApi.retrieve();

      // Call Simulate Accrual And Redemption API to make sure discount can
      // be added.
      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        updatedOrder,
        compCard
      );

      const discountNotApplied = response.discountsNotApplied.find(
        (discountNotApplied) => discountNotApplied.discount.uid === discountId
      );

      if (discountNotApplied) {
        throw new Error(
          `Failed to apply discount. ${discountNotApplied.reason}`
        );
      }

      if (!cancelled) {
        const cardSpecificAppliedDiscounts = response.appliedDiscounts?.filter(
          (disc) => disc?.metadata?.pxCompCardId === cardId?.referenceId
        );
        // Update discounts in redux.
        dispatch(
          fetchCompDiscountsFulfilled(
            response.availableDiscounts,
            cardSpecificAppliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );
        dispatch(addCompDiscountFulfilled());
        dispatch(syncAppliedCompCards(compCard, cardSpecificAppliedDiscounts));
      }
    } catch (error) {
      // Remove discount from order if it was added.
      if (discountId) {
        await orderApi.removeDiscount(discountId);
      }
      dispatch(addCompDiscountRejected(error));
    } finally {
      done();
    }
  },
});

export const clearCompDiscountsLogic = createLogic({
  type: CLEAR_COMP_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;

    try {
      const order = await orderApi.retrieve();
      const paytronixDiscounts = (order.discounts ?? []).filter(
        (discount) =>
          !!discount.metadata?.pxId && !!discount.metadata?.pxCompCardId
      );

      // Remove all Paytronix discounts.
      for (const discount of paytronixDiscounts) {
        if (discount.uid) {
          await orderApi.removeDiscount(discount.uid);
        }
      }
      const updatedOrder = await orderApi.retrieve();

      // Call Simulate Accrual And Redemption API to get updated discounts.
      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        updatedOrder,
        compCard
      );

      // Update discounts in redux.
      dispatch(
        fetchCompDiscountsFulfilled(
          response.availableDiscounts,
          response.appliedDiscounts,
          response.discountsNotApplied,
          response.unavailableDiscounts,
          response.updatedDiscounts
        )
      );

      dispatch(clearCompDiscountsFulfilled());
    } catch (error) {
      dispatch(clearCompDiscountsRejected(error));
    } finally {
      done();
    }
  },
});

export const fetchCompDiscountsLogic = createLogic({
  type: FETCH_COMP_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;

    const order = await orderApi.retrieve();

    if (order) {
      try {
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          order,
          compCard
        );
        const cardSpecificAppliedDiscounts = response.appliedDiscounts?.filter(
          (disc) => disc?.metadata?.pxCompCardId === compCard?.referenceId
        );

        dispatch(
          fetchCompDiscountsFulfilled(
            response.availableDiscounts,
            cardSpecificAppliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );
        dispatch(setPointBalance(response.pointBalance));
      } catch (error) {
        dispatch(fetchCompDiscountsRejected(error));
      } finally {
        done();
      }
    } else {
      if (!order) {
        dispatch(fetchCompDiscountsRejected(new Error('Order is not defined')));
      }
      done();
    }
  },
});

export const removeCompDiscountLogic = createLogic({
  type: REMOVE_COMP_DISCOUNT,
  cancelType: CLEAR_COMP_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    try {
      await orderApi.removeDiscount(action.payload.discountId);
      const updatedOrder = await orderApi.retrieve();

      // Call Simulate Accrual And Redemption API to update available
      // discount.
      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        updatedOrder,
        compCard
      );

      if (!cancelled) {
        const cardSpecificAppliedDiscounts = response.appliedDiscounts?.filter(
          (disc) => disc?.metadata?.pxCompCardId === compCard?.referenceId
        );
        // Update discounts in redux.
        dispatch(
          fetchCompDiscountsFulfilled(
            response.availableDiscounts,
            cardSpecificAppliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );
        dispatch(syncAppliedCompCards(compCard, cardSpecificAppliedDiscounts));
      }

      dispatch(removeCompDiscountFulfilled());
    } catch (error) {
      dispatch(removeCompDiscountRejected(error));
    } finally {
      done();
    }
  },
});

export const removeCompDiscountsLogic = createLogic({
  type: REMOVE_COMP_DISCOUNTS,
  cancelType: CLEAR_COMP_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    try {
      action.payload.discountIds.forEach(async (discountId) => {
        await orderApi.removeDiscount(discountId);
      });
      const updatedOrder = await orderApi.retrieve();

      // Call Simulate Accrual And Redemption API to update available
      // discount.
      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        updatedOrder,
        compCard
      );

      if (!cancelled) {
        const cardSpecificAppliedDiscounts = response.appliedDiscounts?.filter(
          (disc) => disc?.metadata?.pxCompCardId === compCard?.referenceId
        );
        // Update discounts in redux.
        dispatch(
          fetchCompDiscountsFulfilled(
            response.availableDiscounts,
            cardSpecificAppliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );

        dispatch(syncAppliedCompCards(compCard, cardSpecificAppliedDiscounts));
      }

      dispatch(removeCompDiscountsFulfilled());
    } catch (error) {
      dispatch(removeCompDiscountsRejected(error));
    } finally {
      done();
    }
  },
});

export const submitCompDiscountsLogic = createLogic({
  type: SUBMIT_COMP_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const compCard = getState().searchCompCards.selectedCompCard;
    const order = await orderApi.retrieve();

    // Get the card specific details to redeem only on one singular card from order
    const selectedCompCard = getCompCardsFromOrder(order.discounts).filter(
      (cc) => cc.compCardId === compCard?.referenceId
    );

    try {
      if (selectedCompCard.length > 0) {
        // Remove all compcard discounts from order or else PXS will not redeem them
        selectedCompCard[0].compDiscounts.forEach(async (discount) => {
          await orderApi.removeDiscount(discount.squareDiscountId);
        });

        // Get the latest order and use that
        const updatedOrder = await orderApi.retrieve();
        const response = await redeemOnly(
          posContext,
          pxContext,
          updatedOrder,
          selectedCompCard[0]
        );

        response.result === 'failure' &&
          dispatch(
            submitCompDiscountsRejected(new Error(response.errorMessage))
          );
      }
    } catch (error) {
      dispatch(submitCompDiscountsRejected(error));
    } finally {
      done();
    }
  },
});
